import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { API, useAuth } from "../context/AuthContext";
import { ArrowRight, X, Send, HandHelping, Clapperboard, CheckCircle2 } from "lucide-react";

const STATUS_META = {
  new: { label: "New production", chip: "bg-[#FF2E63] text-[#FFF8E7]" },
  in_production: { label: "In production", chip: "bg-[#1A5DFF] text-[#FFF8E7]" },
  completed: { label: "Completed", chip: "bg-[#0A0A0A] text-[#FFD93D]" },
};

const STATUS_FILTERS = [
  { id: "all", label: "All" },
  { id: "new", label: "New" },
  { id: "in_production", label: "In production" },
  { id: "completed", label: "Completed" },
];

export default function Productions() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");
  const [active, setActive] = useState(null); // project being expressed interest in

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/projects`);
        setProjects(r.data || []);
      } catch {
        setError("Could not load productions.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => filter === "all" ? projects : projects.filter((p) => p.status === filter), [projects, filter]);

  const startInterest = (p) => {
    if (!user) {
      navigate(`/signin?next=/productions`);
      return;
    }
    setActive(p);
  };

  return (
    <div className="bg-[#FFF8E7]" data-testid="page-productions">
      {/* HERO */}
      <section className="relative overflow-hidden border-b-2 border-[#0A0A0A]" data-testid="productions-hero">
        <div className="absolute inset-0 halftone-pink opacity-[0.07]" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="inline-block bg-[#1A5DFF] text-[#FFF8E7] px-3 py-1 text-xs uppercase tracking-[0.2em] font-bold mb-4 border-2 border-[#0A0A0A]">Productions</div>
          <h1 className="font-heading font-black text-[#0A0A0A] text-4xl sm:text-5xl lg:text-6xl xl:text-7xl tracking-tight leading-[0.95]">
            Projects we&apos;re<br />
            <span className="text-[#FF2E63] [text-shadow:3px_3px_0_#0A0A0A]">currently backing.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-[#0A0A0A] leading-relaxed">
            New productions are open to crew from our directory. Click <strong>Get involved</strong> on any project tagged
            <span className="inline-block bg-[#FF2E63] text-[#FFF8E7] px-2 mx-1 text-xs uppercase tracking-[0.16em] font-bold border-2 border-[#0A0A0A]">New production</span>
            to offer your skill.
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-10">
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#1A5DFF] mb-3">Browse by status</div>
            <div className="flex flex-wrap gap-3" role="tablist" data-testid="productions-filters">
              {STATUS_FILTERS.map((s) => {
                const active = s.id === filter;
                return (
                  <button
                    key={s.id}
                    role="tab"
                    aria-selected={active}
                    data-testid={`productions-filter-${s.id}`}
                    onClick={() => setFilter(s.id)}
                    className={`px-5 py-2.5 text-sm font-bold uppercase tracking-[0.12em] border-2 border-[#0A0A0A] transition-all ${
                      active ? "bg-[#FF2E63] text-[#FFF8E7] pop-shadow" : "bg-[#FFF8E7] text-[#0A0A0A] hover:bg-[#FFD93D]"
                    }`}
                  >
                    {s.label}
                  </button>
                );
              })}
            </div>
          </div>

          {loading && <div className="text-[#2C2C2C]" data-testid="productions-loading">Loading projects…</div>}
          {error && <div className="text-[#FF2E63]" data-testid="productions-error">{error}</div>}
          {!loading && !error && filtered.length === 0 && (
            <div className="bg-white border-2 border-[#0A0A0A] p-8 text-[#2C2C2C]" data-testid="productions-empty">No projects in this view.</div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8" data-testid="productions-grid">
            {filtered.map((p) => (
              <ProjectCard key={p.id} project={p} user={user} onGetInvolved={() => startInterest(p)} />
            ))}
          </div>
        </div>
      </section>

      {active && <InterestModal project={active} onClose={() => setActive(null)} />}
    </div>
  );
}

function ProjectCard({ project, user, onGetInvolved }) {
  const meta = STATUS_META[project.status] || STATUS_META.new;
  return (
    <article className="bg-white border-2 border-[#0A0A0A] pop-shadow p-6 md:p-8 flex flex-col gap-4" data-testid={`project-card-${project.id}`}>
      <div className="flex items-center gap-3">
        <span className={`text-xs uppercase tracking-[0.16em] font-bold px-3 py-1 border-2 border-[#0A0A0A] ${meta.chip}`} data-testid={`project-status-${project.id}`}>{meta.label}</span>
        <span className="text-xs uppercase tracking-[0.14em] text-[#2C2C2C]">{project.year}</span>
      </div>
      <div>
        <h3 className="font-heading font-bold text-2xl text-[#0A0A0A] leading-tight">{project.title}</h3>
        <div className="text-sm text-[#2C2C2C] mt-1">Dir. {project.director}</div>
      </div>
      <p className="text-[#2C2C2C] leading-relaxed flex-1">{project.description}</p>
      {project.status === "new" && project.looking_for_roles?.length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-[0.16em] font-bold text-[#1A5DFF] mb-2">Looking for</div>
          <div className="flex flex-wrap gap-2">
            {project.looking_for_roles.map((r) => (
              <span key={r} className="text-xs uppercase tracking-[0.14em] px-2 py-1 bg-[#FFE8A8] text-[#0A0A0A] border-2 border-[#0A0A0A]" data-testid={`project-role-${project.id}-${r}`}>{r}</span>
            ))}
          </div>
        </div>
      )}
      <div className="pt-3 border-t-2 border-[#0A0A0A] mt-auto">
        {project.status === "new" ? (
          <button
            onClick={onGetInvolved}
            data-testid={`project-get-involved-${project.id}`}
            className="w-full sm:w-auto bg-[#FF2E63] text-[#FFF8E7] hover:bg-[#E0204F] border-2 border-[#0A0A0A] pop-shadow rounded-none px-5 py-3 text-sm font-medium inline-flex items-center justify-center gap-2"
          >
            <HandHelping className="w-4 h-4" /> {user ? "Get involved" : "Sign in to get involved"} <ArrowRight className="w-4 h-4" />
          </button>
        ) : (
          <div className="text-xs uppercase tracking-[0.16em] font-bold text-[#2C2C2C] inline-flex items-center gap-1.5">
            {project.status === "in_production" ? <Clapperboard className="w-4 h-4 text-[#1A5DFF]" /> : <CheckCircle2 className="w-4 h-4 text-[#0A0A0A]" />}
            {project.status === "in_production" ? "Currently shooting · crew locked" : "Project complete · on the circuit"}
          </div>
        )}
      </div>
    </article>
  );
}

function InterestModal({ project, onClose }) {
  const [role, setRole] = useState((project.looking_for_roles && project.looking_for_roles[0]) || "");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(null); setSubmitting(true);
    try {
      await axios.post(`${API}/projects/${project.id}/interest`, { role_offered: role, message }, { withCredentials: true });
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not submit. Please try again.");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#0A0A0A]/80 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="interest-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-[#FFF8E7] border-2 border-[#0A0A0A] pop-shadow max-w-xl w-full p-8 my-8">
        <div className="flex items-start justify-between mb-5">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#FF2E63] mb-1">Get involved</div>
            <h2 className="font-heading font-black text-[#0A0A0A] text-2xl leading-tight">{project.title}</h2>
            <div className="text-sm text-[#2C2C2C]">Dir. {project.director}</div>
          </div>
          <button onClick={onClose} aria-label="Close" data-testid="interest-close" className="bg-[#0A0A0A] text-[#FFF8E7] hover:bg-[#FF2E63] p-2 border-2 border-[#0A0A0A]"><X className="w-4 h-4" /></button>
        </div>

        {done ? (
          <div className="bg-[#1A5DFF] text-[#FFF8E7] border-2 border-[#0A0A0A] p-6" data-testid="interest-success">
            <CheckCircle2 className="w-10 h-10 mb-3" />
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#FFD93D] mb-2">Interest registered</div>
            <h3 className="font-heading font-black text-2xl tracking-tight leading-tight">We&apos;ll be in touch.</h3>
            <p className="mt-3 text-[#FFE8A8]">The producer will reach out to you directly. Thanks for offering your skill — this is how the directory pays back.</p>
            <button onClick={onClose} className="mt-5 bg-[#FFD93D] text-[#0A0A0A] hover:bg-[#FFCB05] border-2 border-[#0A0A0A] rounded-none px-5 py-2.5 text-sm font-medium">Close</button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="interest-form">
            <p className="text-sm text-[#2C2C2C]">
              {project.looking_for_roles?.length > 0 ? `This project is looking for: ${project.looking_for_roles.join(", ")}.` : "Tell us what you can bring to this production."}
            </p>
            <label className="flex flex-col gap-1.5">
              <span className="text-xs uppercase tracking-[0.16em] font-bold text-[#0A0A0A]">Role / skill you&apos;re offering <span className="text-[#FF2E63]">*</span></span>
              {project.looking_for_roles?.length > 0 ? (
                <select data-testid="interest-role" value={role} onChange={(e) => setRole(e.target.value)} required className={cls}>
                  {project.looking_for_roles.map((r) => <option key={r} value={r}>{r}</option>)}
                  <option value="Other">Other (specify in message)</option>
                </select>
              ) : (
                <input data-testid="interest-role" value={role} onChange={(e) => setRole(e.target.value)} required className={cls} placeholder="e.g. Editor" />
              )}
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-xs uppercase tracking-[0.16em] font-bold text-[#0A0A0A]">Short message <span className="text-[#2C2C2C] font-normal normal-case tracking-normal">(optional)</span></span>
              <textarea data-testid="interest-message" rows={4} value={message} onChange={(e) => setMessage(e.target.value)} className={cls} placeholder="A line or two about your experience, availability, or anything else useful."></textarea>
            </label>

            {error && <div className="bg-[#FF2E63] text-[#FFF8E7] border-2 border-[#0A0A0A] p-3 text-sm" role="alert" data-testid="interest-error">{error}</div>}

            <div className="flex gap-3 justify-end pt-2">
              <button type="button" onClick={onClose} className="bg-transparent border-2 border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-[#FFF8E7] rounded-none px-5 py-2.5 text-sm font-medium">Cancel</button>
              <button type="submit" disabled={submitting} data-testid="interest-submit" className="bg-[#FF2E63] text-[#FFF8E7] hover:bg-[#E0204F] disabled:opacity-60 border-2 border-[#0A0A0A] pop-shadow rounded-none px-5 py-2.5 text-sm font-medium inline-flex items-center gap-2"><Send className="w-4 h-4" /> {submitting ? "Sending…" : "Send"}</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

const cls = "w-full bg-white border-2 border-[#0A0A0A] text-[#0A0A0A] px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#FF2E63] rounded-none";
